import React, { useEffect } from 'react';
import { reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import { withErrorBoundary } from 'react-error-boundary';
import GenericViewError from 'components/Error/GenericViewError';
import Filters from 'components/LearningPath/Filters';
import LearningPathList from 'components/LearningPath/List';
import { handleError } from 'services/ErrorService';
import CatalogIcon from '@patternfly/react-icons/dist/esm/icons/catalog-icon';
import {
  CatalogLayout,
  CenteredLoadingSpinner,
  useFeature,
} from '@ole-ui/ole-ui-components';
import useMobxStores from 'hooks/useMobxStores';
import { EnablementFeatureFlags } from 'config/featureFlags';
import SkillsPathForbiddenView from 'views/SkillsPath/ForbiddenView';
import FilterChips from 'views/Catalog/FilterChips';

import '../Catalog/styles.scss';
import './styles.scss';
import {
  EmptyState,
  EmptyStateBody,
  EmptyStateIcon,
  Title,
} from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';

const LearningPathCatalog = () => {
  const { t } = useTranslation();
  const isSkillsPathsEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.SkillsPathsEnabled,
    defaultFlagValue: false,
  });
  const {
    featuresStore,
    learningPathStore,
    routerStore,
    searchStore,
    userStore,
    organizationStore,
  } = useMobxStores();

  const route = routerStore?.route as any;
  const pageTitle = route?.params?.title;

  const applyPresetFiltersFromRoute = (params: Record<string, string> = {}) => {
    learningPathStore.clearFilters(undefined, false);
    searchStore.query = params?.q || '';
    learningPathStore.presetFilters(params);
    learningPathStore.recalculateFilterCounts();
  };

  useEffect(() => {
    learningPathStore.init(userStore);

    // Populate entry data and filters on initial load
    when(
      () =>
        userStore.isOrganizationLeader &&
        organizationStore.organizations?.length > 0 &&
        !userStore.subscription.subscription,
      async () => {
        await learningPathStore.init(userStore);
      },
    );
  }, []);

  useEffect(() => {
    // Populate filter options once entries are loaded, then apply route params.
    // getFilterInfo() replaces filters (all unchecked); re-apply route params so
    // deep links stay in sync on direct navigation.
    when(
      () => learningPathStore.loaded,
      async () => {
        await learningPathStore.getFilterInfo();
        if (routerStore?.route?.name === 'skills-paths') {
          const params =
            (routerStore.route as { params?: Record<string, string> })
              ?.params ?? {};
          applyPresetFiltersFromRoute(params);
        }
      },
    );
  }, []);

  // Sync filters from route whenever we're on skills-paths (MobX reaction so we
  // always read latest route; same pattern as Catalog).
  useEffect(() => {
    const dispose = reaction(
      () => {
        const r = routerStore?.route as
          | { name?: string; params?: Record<string, string> }
          | undefined;
        if (r?.name !== 'skills-paths' || !learningPathStore.filterInfoLoaded)
          return null;
        return JSON.stringify(r?.params ?? {});
      },
      (paramsKey) => {
        if (paramsKey === null) return;
        const params =
          (routerStore?.route as { params?: Record<string, string> })?.params ??
          {};
        applyPresetFiltersFromRoute(params);
      },
      { fireImmediately: true },
    );
    return () => dispose();
  }, []);

  const newCatalogEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.NewCatalog,
    defaultFlagValue: false,
  });

  if (!featuresStore.allFeaturesFetched) {
    return (
      <>
        <Helmet title={`${pageTitle} - Red Hat Learning`} />
        <CatalogLayout>
          <CatalogLayout.Content className="catalog__content">
            <div className="learning-paths__loading-container">
              <CenteredLoadingSpinner />
            </div>
          </CatalogLayout.Content>
        </CatalogLayout>
      </>
    );
  }

  if (!isSkillsPathsEnabled && !userStore.isOrganizationLeader) {
    return (
      <>
        <Helmet title={`${pageTitle} - Red Hat Learning`} />
        <SkillsPathForbiddenView />
      </>
    );
  }

  const renderContent = () => {
    if (!learningPathStore.loaded && learningPathStore.loading) {
      return (
        <div className="learning-paths__loading-container">
          <CenteredLoadingSpinner />
        </div>
      );
    }

    if (!learningPathStore.filteredEntries?.length) {
      return (
        <EmptyState>
          <EmptyStateIcon icon={CatalogIcon} />
          <Title size="lg" headingLevel="h4">
            {t('No Skills Paths Found')}
          </Title>
          <EmptyStateBody>
            {t(
              'No results match the filter criteria. Change the filters and try again.',
            )}
          </EmptyStateBody>
        </EmptyState>
      );
    }

    return <LearningPathList />;
  };

  return (
    <>
      <Helmet title={`${pageTitle} - Red Hat Learning`} />

      <CatalogLayout>
        <CatalogLayout.Filters count={learningPathStore.filteredCount}>
          {learningPathStore.filterInfoLoaded && <Filters />}
        </CatalogLayout.Filters>
        {newCatalogEnabled ? (
          <CatalogLayout.Content className="catalog__content">
            <div className="catalog__filter-chips-container">
              <FilterChips filterStore="learningPath" />
            </div>
            {renderContent()}
          </CatalogLayout.Content>
        ) : (
          <CatalogLayout.Content>{renderContent()}</CatalogLayout.Content>
        )}
      </CatalogLayout>
    </>
  );
};

export default withErrorBoundary(
  observer(LearningPathCatalog),
  GenericViewError,
  handleError,
);
